/**
 * directoryMapLayers: which of the Directory map's three layers open on.
 *
 * The rule, one pure function so it is pinned in one place:
 *   - My clients opens on for a seat with a caseload (whoami.has_caseload)
 *     that holds the clinical read (the client layer's own floor). Every
 *     other seat opens with it off, and a seat without the clinical read
 *     has no client layer at all (the Directory Editor at its defaults).
 *   - Organizations and People open OFF only for that same seat, and only
 *     while nothing narrows or aims the Directory: no filter, no search
 *     text and no client named in the URL (?near). In every other case
 *     they open on, as they always have.
 *
 * Until the seat is known (whoami still loading) nothing opens on, so a
 * cold load straight onto the map never draws the Directory and then
 * takes it away a moment later.
 *
 * The rule is the default only. A hand toggle on a layer pill overrides
 * it for that layer until the person leaves the Directory
 * (resolveDirectoryMapLayers), so choosing a filter turns the Directory
 * layers on unless the person already switched one by hand.
 */

export interface DirectoryMapLayers {
  clients: boolean;
  organizations: boolean;
  people: boolean;
}

export type DirectoryMapLayerKey = keyof DirectoryMapLayers;

/** A layer the person switched by hand; an absent key follows the rule. */
export type DirectoryMapLayerOverrides = Partial<DirectoryMapLayers>;

export interface DirectoryMapLayerInputs {
  /** whoami has landed: the two seat inputs below are real. */
  seatKnown: boolean;
  /** read_clinical_record: the client layer exists for this seat. */
  canShowClients: boolean;
  /** whoami.has_caseload: the seat is responsible for an active client. */
  hasCaseload: boolean;
  /** A Directory filter pill is set. */
  filtersActive: boolean;
  /** The Directory search box holds text. */
  searchActive: boolean;
  /** The URL names a client (?near). */
  nearActive: boolean;
}

export function directoryMapLayerDefaults(inputs: DirectoryMapLayerInputs): DirectoryMapLayers {
  if (!inputs.seatKnown) return { clients: false, organizations: false, people: false };
  const ownClients = inputs.canShowClients && inputs.hasCaseload;
  const directoryAsked = inputs.filtersActive || inputs.searchActive || inputs.nearActive;
  const directoryOn = !ownClients || directoryAsked;
  return { clients: ownClients, organizations: directoryOn, people: directoryOn };
}

export function resolveDirectoryMapLayers(
  defaults: DirectoryMapLayers,
  overrides: DirectoryMapLayerOverrides,
): DirectoryMapLayers {
  return {
    clients: overrides.clients ?? defaults.clients,
    organizations: overrides.organizations ?? defaults.organizations,
    people: overrides.people ?? defaults.people,
  };
}

/** The quiet line on the map while neither Directory layer draws. */
export const DIRECTORY_LAYERS_OFF_TEXT = 'Directory listings are off. Turn on Organizations or People, or choose a filter.';
